import { useState } from 'react';
import { useCustomMutation } from '@refinedev/core';
import { Plus, Trash2, Loader2, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import type { LeadCustomField } from '../../types';

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Текст' },
  { value: 'number', label: 'Число' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Телефон' },
  { value: 'url', label: 'URL' },
  { value: 'date', label: 'Дата' },
  { value: 'boolean', label: 'Да/Нет' },
];

interface NewFieldState {
  name: string;
  label: string;
  fieldType: string;
  isRequired: boolean;
}

interface CustomFieldsEditorProps {
  webhookId: number;
  fields: LeadCustomField[];
  onRefetch: () => void;
}

export function CustomFieldsEditor({ webhookId, fields, onRefetch }: CustomFieldsEditorProps) {
  const [newField, setNewField] = useState<NewFieldState>({
    name: '',
    label: '',
    fieldType: 'text',
    isRequired: false,
  });

  const addMutation = useCustomMutation();
  const deleteMutation = useCustomMutation();

  const handleAdd = () => {
    if (!newField.name.trim()) { return; }
    addMutation.mutate(
      {
        url: `/leads/webhooks/${webhookId}/custom-fields`,
        method: 'post',
        values: {
          name: newField.name,
          label: newField.label || newField.name,
          fieldType: newField.fieldType,
          isRequired: newField.isRequired,
          position: fields.length,
        },
      },
      {
        onSuccess: () => {
          setNewField({ name: '', label: '', fieldType: 'text', isRequired: false });
          onRefetch();
        },
      },
    );
  };

  const handleDelete = (fieldId: number) => {
    deleteMutation.mutate(
      {
        url: `/leads/webhooks/${webhookId}/custom-fields/${fieldId}`,
        method: 'delete',
        values: {},
      },
      { onSuccess: onRefetch },
    );
  };

  return (
    <div className="space-y-4">
      {/* Add new field */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        <div>
          <label htmlFor="cf-name" className="text-xs text-muted-foreground mb-1 block">Имя (key)</label>
          <Input
            placeholder="field_name"
            value={newField.name}
            onChange={(e) => setNewField((prev) => ({ ...prev, name: e.target.value }))}
            className="h-8 text-sm font-mono"
          />
        </div>
        <div>
          <label htmlFor="field-label" className="text-xs text-muted-foreground mb-1 block">Метка</label>
          <Input
            id="field-label"
            placeholder="Название поля"
            value={newField.label}
            onChange={(e) => setNewField((prev) => ({ ...prev, label: e.target.value }))}
            className="h-8 text-sm"
          />
        </div>
        <div>
          <label htmlFor="field-type" className="text-xs text-muted-foreground mb-1 block">Тип</label>
          <Select
            value={newField.fieldType}
            onValueChange={(v) => setNewField((prev) => ({ ...prev, fieldType: v }))}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          size="sm"
          onClick={handleAdd}
          disabled={!newField.name.trim() || addMutation.mutation.isPending}
          className="h-8"
        >
          {addMutation.mutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          <span className="ml-1">Добавить</span>
        </Button>
      </div>

      {/* Fields table */}
      {fields.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Имя</TableHead>
              <TableHead>Метка</TableHead>
              <TableHead>Тип</TableHead>
              <TableHead>Обязательное</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field) => (
              <TableRow key={field.id}>
                <TableCell>
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                </TableCell>
                <TableCell className="font-mono text-xs">{field.name}</TableCell>
                <TableCell className="text-sm">{field.label ?? field.name}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className="text-xs">
                    {FIELD_TYPE_OPTIONS.find((o) => o.value === field.fieldType)?.label ?? field.fieldType ?? 'text'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={field.isRequired ? 'default' : 'outline'} className="text-xs">
                    {field.isRequired ? 'Да' : 'Нет'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => handleDelete(field.id)}
                    disabled={deleteMutation.mutation.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <p className="text-sm text-muted-foreground py-4 text-center">Нет кастомных полей</p>
      )}
    </div>
  );
}
